import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Stack,
  Button,
  LinearProgress,
  useTheme,
  alpha,
  Grid,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  Chip,
  CircularProgress,
  Tooltip,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Edit,
  BusinessCenter,
  Warning,
  CheckCircle,
  Cancel,
  ArrowForward,
  PendingOutlined,
  CheckCircleOutline,
  CancelOutlined,
  ExpandMore,
  PictureAsPdf,
  MonetizationOn,
  CalendarMonth,
  Person,
  Phone,
  CalendarToday,
  Business,
  AccountBalance,
  FileDownload,
  Email,
  Add,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchStartupDetail, changeStartupStatus, fetchCommittees, updateStartupDates, updateStartupShortlinkPath, shareStartup, activePreFunding, deactivePreFunding, deactiveShowing, activeShowing, deactiveDetail, activeDetail } from '../store/slices/adminSlice';
import { getFileLink } from '../utils/getFileLink';
import { toast } from 'react-toastify';
import { ApiHelper } from '../utils/apiHelper';
import SingleFileUpload from '../components/fileUpload/FileUpload';
import XLSX from 'xlsx-js-style';

const STATUS_CONFIG = {
  PENDING: {
    label: 'Beklemede',
    color: 'default',
    next: null
  },
  APPROVED: {
    label: 'Onaylandı',
    color: 'success',
    next: null,
    finalStates: {
      SUCCESS: 'COMPLETED_SUCCESS',
      FAILURE: 'COMPLETED_FAILURE'
    }
  },
  REJECTED: {
    label: 'Reddedildi',
    color: 'error',
    next: null
  },
  WAITING_REVIEW: {
    label: 'İnceleme Bekliyor',
    color: 'warning',
    next: 'WAITING_INVESTMENT_COMMITTEE',
    nextLabel: 'Yatırım Komitesine Gönder'
  },
  WAITING_INVESTMENT_COMMITTEE: {
    label: 'Yatırım Komitesi İncelemesinde',
    color: 'info',
    next: 'WAITING_MKK_APPROVAL',
    nextLabel: 'KFS Onayına Gönder'
  },
  WAITING_MKK_APPROVAL: {
    label: 'KFS Onayı Bekliyor',
    color: 'secondary',
    next: 'WAITING_TAKASBANK_APPROVAL',
    nextLabel: 'Takasbank Onayına Gönder'
  },
  WAITING_TAKASBANK_APPROVAL: {
    label: 'Takasbank Onayı Bekliyor',
    color: 'primary',
    next: 'TAKASBANK_APPROVED',
    nextLabel: 'Takasbank Onaylandı'
  },
  TAKASBANK_APPROVED: {
    label: 'Takasbank Onaylandı',
    color: 'success',
    next: 'LAST_VIEW',
    nextLabel: 'Son İnceleme'
  },
  LAST_VIEW: {
    label: 'Son İnceleme',
    color: 'default',
    next: null
  },
  COMPLETED_SUCCESS: {
    label: 'Başarıyla Tamamlandı',
    color: 'success',
    next: null
  },
  COMPLETED_FAILURE: {
    label: 'Başarısız Tamamlandı',
    color: 'error',
    next: null
  }
};

const COMMITTEE_LABELS = {
  FINANCIAL_COMMITTEE: 'Finansal Komite',
  TECHNICAL_COMMITTEE: 'Teknik Komite',
  MARKETING_COMMITTEE: 'Pazarlama Komitesi',
  LEGAL_COMMITTEE: 'Hukuk Komitesi',
  OTHER_COMMITTEE: 'Diğer Komite',
};

const StartupDetailPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { startupId } = useParams();
  const { selectedStartup, committees, loading } = useSelector((state) => state.admin);
  const { adminInfo } = useSelector((state) => state.auth);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [openDateDialog, setOpenDateDialog] = useState(false);
  const [preFundDialog, setOpenPreFundDialog] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [preFundEndDate, setPreFundEndDate] = useState(false);
  const [endDate, setEndDate] = useState('');

  // Committee evaluation states
  const [openEvaluationDialog, setOpenEvaluationDialog] = useState(false);
  const [evaluationText, setEvaluationText] = useState('');

  // Komite formu için state'ler
  const [formDescription, setFormDescription] = useState('');
  const [formStatus, setFormStatus] = useState('APPROVED');
  const [formDocument, setFormDocument] = useState('');

  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  // Add new states for KFS preview
  const [openKfsPreviewDialog, setOpenKfsPreviewDialog] = useState(false);
  const [kfsPreviewData, setKfsPreviewData] = useState(null);

  // Add new state for publish confirmation
  const [openPublishDialog, setOpenPublishDialog] = useState(false);

  // Add new states for campaign finalization
  const [openSuccessFinishDialog, setOpenSuccessFinishDialog] = useState(false);
  const [openFailureFinishDialog, setOpenFailureFinishDialog] = useState(false);

  // Add new state for countdown
  const [countdown, setCountdown] = useState(null);

  // Add new state for approved investments
  const [approvedInvestments, setApprovedInvestments] = useState([]);
  const [loadingInvestments, setLoadingInvestments] = useState(false);

  // Campaign documents states
  const [openDocumentsDialog, setOpenDocumentsDialog] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  const [isLoading, setIsLoading] = useState(false); // Add a state for loader

  const [preFundData, setPreFundData] = useState(null);
  const [preFundDataInfo, setPreFundDataInfo] = useState(null);
  const [loadingPreFundDataInfo, setLoadingPreFundDataInfo] = useState(false);

  // Kısa link yolu (shortlink path) state
  const [shortlinkPathInput, setShortlinkPathInput] = useState('');
  const [shortlinkPathError, setShortlinkPathError] = useState('');
  const [shortlinkPathSaving, setShortlinkPathSaving] = useState(false);

  // Load initial evaluation when startup data is available
  useEffect(() => {
    if (selectedStartup?.committeeEvaluation) {
      setEvaluationText(selectedStartup.committeeEvaluation);
    }
  }, [selectedStartup]);

  // Tarih formatı için yardımcı fonksiyon
  const formatDateForInput = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(Number(timestamp));
      if (isNaN(date.getTime())) return '';
      return date.toISOString().slice(0, 16);
    } catch (error) {
      console.error('Date formatting error:', error);
      return '';
    }
  };

  // Seçilen startup'ın tarihlerini yükle
  useEffect(() => {
    if (selectedStartup) {
      if (selectedStartup.startDate) {
        try {
          const startTimestamp = Number(selectedStartup.startDate);
          if (!isNaN(startTimestamp)) {
            setStartDate(startTimestamp);
          }
        } catch (error) {
          console.error('Error parsing start date:', error);
        }
      }

      if (selectedStartup.endDate) {
        try {
          const endTimestamp = Number(selectedStartup.endDate);
          if (!isNaN(endTimestamp)) {
            setEndDate(endTimestamp);
          }
        } catch (error) {
          console.error('Error parsing end date:', error);
        }
      }

      setShortlinkPathInput(selectedStartup.shortlinkPath ?? '');
      setShortlinkPathError('');
    }
  }, [selectedStartup]);

  useEffect(() => {
    if (startupId) {
      dispatch(fetchStartupDetail(startupId));
    }
  }, [dispatch, startupId]);

  // Debug için log ekleyelim
  useEffect(() => {
    console.log('Debug Values:', {
      adminType: adminInfo?.type,
      startupStatus: selectedStartup?.status,
      isAdmin: adminInfo?.type === 'SUPER_ADMIN',
      isWaitingReview: selectedStartup?.status === 'WAITING_REVIEW'
    });
  }, [adminInfo, selectedStartup]);

  // Komiteleri sadece WAITING_INVESTMENT_COMMITTEE durumunda yükle
  useEffect(() => {
    console.log(adminInfo)
    if (startupId && selectedStartup?.status === 'WAITING_INVESTMENT_COMMITTEE' && 
        (adminInfo?.type?.includes('COMMITTEE') || adminInfo?.type?.includes('SUPER_ADMIN'))) {
      dispatch(fetchCommittees(startupId));
    }
  }, [dispatch, startupId, selectedStartup?.status, adminInfo]);

  // Komite bilgisi geldiğinde form state'lerini güncelle
  useEffect(() => {
    if (committees?.length > 0 && adminInfo?.type) {
      const relevantCommittee = committees.find(
        committee => committee.committeeType === adminInfo.type
      );

      if (relevantCommittee) {
        setFormDescription(relevantCommittee.description || '');
        setFormStatus(relevantCommittee.status || 'APPROVED');
        setFormDocument(relevantCommittee.documentPath || '');
        
        console.log('Initializing form with committee data:', {
          description: relevantCommittee.description,
          status: relevantCommittee.status,
          document: relevantCommittee.documentPath
        });
      }
    }
  }, [committees, adminInfo]);

  // Debug için log ekleyelim
  useEffect(() => {
    if (selectedStartup) {
      console.log('Selected Startup:', selectedStartup);
      console.log('Current Startup Status:', selectedStartup.status);
      console.log('Status Config:', STATUS_CONFIG[selectedStartup.status]);
      console.log('Next Status:', STATUS_CONFIG[selectedStartup.status]?.next);
    }
  }, [selectedStartup]);

  const handleStatusChange = async () => {
    const currentStatus = selectedStartup?.status;
    console.log('Attempting to change status from:', currentStatus);
    
    try {
      if (currentStatus === 'LAST_VIEW') {
        setOpenPublishDialog(true);
      } else {
        const nextStatus = STATUS_CONFIG[currentStatus]?.next;
        console.log('To status:', nextStatus);
        
        if (nextStatus && selectedStartup?.startupId) {
          await dispatch(changeStartupStatus(selectedStartup.startupId, nextStatus));
          toast.success('Kampanya durumu başarıyla güncellendi');
        }
      }
      // Refresh startup details after status change
      dispatch(fetchStartupDetail(selectedStartup.startupId));
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error(error.message || 'Kampanya durumu güncellenirken bir hata oluştu');
    }
  };

  const handlePublishConfirm = async () => {
    try {
      await dispatch(shareStartup(selectedStartup.startupId)).unwrap();
      toast.success('Kampanya başarıyla yayınlandı');
      setOpenPublishDialog(false);
      dispatch(fetchStartupDetail(selectedStartup.startupId));
    } catch (error) {
      console.error('Failed to publish campaign:', error);
      toast.error(error.message || 'Kampanya yayınlanırken bir hata oluştu');
    }
  };

  const handleFileUpload = (filePath) => {
    console.log('Uploaded file path:', filePath);
    setFormDocument(filePath);
  };

  const handleCommitteeSubmit = async () => {
    const userCommitteeType = adminInfo?.type;
    const relevantCommittee = committees?.find(
      committee => committee.committeeType === userCommitteeType
    );

    if (!relevantCommittee?.committeeId) {
      toast.error('Komite bilgisi bulunamadı');
      return;
    }

    try {
      setIsLoading(true); // Show loader
      const formData = new URLSearchParams();
      formData.append('committeeId', relevantCommittee.committeeId);
      formData.append('documentPath', formDocument);
      formData.append('description', formDescription);
      formData.append('status', formStatus);

      await ApiHelper.put('/admin/committee', formData.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      
      // Komiteleri yeniden yükle
      dispatch(fetchCommittees(selectedStartup.startupId));
      
      // Form state'lerini temizle
      setFormDescription('');
      setFormDocument('');
      setFormStatus('APPROVED');
      
      toast.success('Komite kararı başarıyla kaydedildi');
    } catch (error) {
      console.error('Committee approval error:', error);
      toast.error('Komite kararı kaydedilirken bir hata oluştu');
    } finally {
      setIsLoading(false); // Hide loader
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Lütfen red sebebi giriniz');
      return;
    }

    try {
      await dispatch(changeStartupStatus(
        selectedStartup.startupId,
        'REJECTED',
        rejectionReason
      ));
      setOpenRejectDialog(false);
      setRejectionReason('');
      // Reddetme işleminden sonra detayları tekrar çek
      dispatch(fetchStartupDetail(selectedStartup.startupId));
      toast.success('Kampanya başarıyla reddedildi');
    } catch (error) {
      toast.error('Kampanya reddedilirken bir hata oluştu');
    }
  };

  const handleFinishCampaign = async (finishType) => {
    try {
      const newStatus = STATUS_CONFIG.APPROVED.finalStates[finishType];
      await dispatch(changeStartupStatus(selectedStartup.startupId, newStatus));
      toast.success(
        finishType === 'SUCCESS' 
          ? 'Kampanya başarıyla sonlandırıldı' 
          : 'Kampanya başarısız olarak sonlandırıldı', 
        { toastId: finishType === 'SUCCESS' ? 'success' : 'warning' }
      );
      setOpenSuccessFinishDialog(false);
      setOpenFailureFinishDialog(false);
      dispatch(fetchStartupDetail(selectedStartup.startupId));
    } catch (error) {
      console.error('Failed to finish campaign:', error);
      toast.error('Kampanya sonlandırılırken bir hata oluştu');
    }
  };

  const handleFundingControl = async (action) => {
    try {
      await ApiHelper.post(`/admin/${action}-startup-funding/${selectedStartup.startupId}`);
      toast.success(
        action === 'stop' ? 'Kampanya fonlaması durduruldu' : 'Kampanya fonlaması başlatıldı', 
        { toastId: 'success' }
      );
      // Refresh startup details
      dispatch(fetchStartupDetail(selectedStartup.startupId));
    } catch (error) {
      console.error('Funding control error:', error);
      toast.error(
        'İşlem sırasında bir hata oluştu', 
        { toastId: 'error' }
      );
    }
  };

  // Kısa link yolu: sadece a-z, A-Z, 0-9 ve tire
  const SHORTLINK_PATH_REGEX = /^[a-zA-Z0-9-]*$/;
  const handleShortlinkPathChange = (e) => {
    const value = e.target.value;
    if (value === '' || SHORTLINK_PATH_REGEX.test(value)) {
      setShortlinkPathInput(value);
      setShortlinkPathError('');
    }
  };
  const handleSaveShortlinkPath = async () => {
    setShortlinkPathError('');
    const trimmed = shortlinkPathInput.trim();
    if (!SHORTLINK_PATH_REGEX.test(trimmed) && trimmed !== '') {
      setShortlinkPathError('Sadece İngilizce harf, rakam ve tire kullanılabilir. Boşluk ve özel karakter kullanılamaz.');
      return;
    }
    setShortlinkPathSaving(true);
    try {
      await dispatch(updateStartupShortlinkPath({
        startupId: selectedStartup.startupId,
        shortlinkPath: trimmed,
      })).unwrap();
      toast.success('Kısa link yolu güncellendi');
    } catch (error) {
      const message = error?.message || 'Kısa link yolu güncellenirken bir hata oluştu';
      setShortlinkPathError(message);
      toast.error(message);
    } finally {
      setShortlinkPathSaving(false);
    }
  };

  const handleExportToExcel = () => {
    if (!preFundDataInfo || preFundDataInfo.length === 0) {
      toast.error('Export edilecek veri bulunamadı');
      return;
    }

    try {
      // Prepare data for Excel export
      const exportData = preFundDataInfo.map((item, index) => ({
        'Sıra No': index + 1,
        'Yatırım ID': item.investmentId || '-',
        'Kampanya Adı': item.startupName || '-',
        'Yatırımcı Adı': item.investorName || '-',
        'Yatırım Tutarı (₺)': item.amount ? Number(item.amount).toLocaleString('tr-TR') : '-',
        'Güncelleme Tarihi': item.updatedAt ? new Date(item.updatedAt).toLocaleString('tr-TR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }) : '-'
      }));

      // Add summary row
      const totalAmount = preFundDataInfo.reduce((sum, item) => sum + Number(item.amount || 0), 0);
      exportData.push({
        'Sıra No': '',
        'Yatırım ID': '',
        'Kampanya Adı': 'TOPLAM',
        'Yatırımcı Adı': '',
        'Yatırım Tutarı (₺)': Number(totalAmount).toLocaleString('tr-TR'),
        'Güncelleme Tarihi': ''
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Style the header row
      const headerStyle = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "1976D2" } },
        alignment: { horizontal: "center", vertical: "center" }
      };

      // Apply styles to header row
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (!ws[cellAddress]) ws[cellAddress] = {};
        ws[cellAddress].s = headerStyle;
      }

      // Style the total row
      const totalRowIndex = exportData.length - 1;
      const totalRowStyle = {
        font: { bold: true, color: { rgb: "000000" } },
        fill: { fgColor: { rgb: "E3F2FD" } }
      };

      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: totalRowIndex, c: col });
        if (ws[cellAddress]) {
          ws[cellAddress].s = totalRowStyle;
        }
      }

      // Set column widths
      ws['!cols'] = [
        { width: 10 }, // Sıra No
        { width: 35 }, // Yatırım ID
        { width: 30 }, // Kampanya Adı
        { width: 30 }, // Yatırımcı Adı
        { width: 20 }, // Yatırım Tutarı
        { width: 25 }  // Güncelleme Tarihi
      ];

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Ön Talep Yatırımları');

      // Generate filename with current date
      const currentDate = new Date().toLocaleDateString('tr-TR').replace(/\./g, '-');
      const startupName = selectedStartup?.name || 'Startup';
      const filename = `On_Talep_Yatirimlari_${startupName}_${currentDate}.xlsx`;

      // Save the file
      XLSX.writeFile(wb, filename);
      
      toast.success('Excel dosyası başarıyla indirildi');
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error('Excel dosyası oluşturulurken bir hata oluştu');
    }
  };

  const renderCommitteeApprovalForm = () => {
    const userCommitteeType = adminInfo?.type;
    const relevantCommittee = committees?.find(
      committee => committee.committeeType === userCommitteeType
    );

    if (!relevantCommittee) return null;

    const isDisabled = relevantCommittee.status !== 'PENDING';
    const committeeLabel = COMMITTEE_LABELS[userCommitteeType] || userCommitteeType;

    // Debug için log ekleyelim
    console.log('Form Values:', {
      description: formDescription,
      status: formStatus,
      document: formDocument,
      isDisabled
    });

    return (
      <Card 
        sx={{ 
          mb: 4,
          borderRadius: 2,
          position: 'relative',
          overflow: 'visible',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.warning.main})`,
            borderRadius: '2px 2px 0 0',
          }
        }}
      >
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  }}
                >
                  <Edit sx={{ color: theme.palette.primary.main }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography variant="h6" gutterBottom>
                      Komite Değerlendirme Formu
                    </Typography>
                    <Chip
                      label={committeeLabel}
                      color="primary"
                      size="small"
                      sx={{
                        fontWeight: 'medium',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.dark,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        '& .MuiChip-label': {
                          px: 2,
                        },
                      }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {isDisabled 
                      ? `${committeeLabel} değerlendirmesi tamamlanmıştır.`
                      : `${committeeLabel} olarak değerlendirmenizi yapın ve kararınızı belirtin.`}
                  </Typography>
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Değerlendirme Açıklaması"
                value={formDescription || relevantCommittee.description || ''}
                onChange={(e) => setFormDescription(e.target.value)}
                disabled={isDisabled}
                multiline
                rows={4}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'background.paper',
                    '&:hover': {
                      '& > fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack spacing={3}>
                <FormControl 
                  fullWidth 
                  disabled={isDisabled}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                    },
                  }}
                >
                  <InputLabel>Karar</InputLabel>
                  <Select
                    value={formStatus || relevantCommittee.status || 'APPROVED'}
                    onChange={(e) => setFormStatus(e.target.value)}
                    label="Karar"
                  >
                    <MenuItem value="APPROVED">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <CheckCircle sx={{ color: theme.palette.success.main }} />
                        <Typography>Onay</Typography>
                      </Stack>
                    </MenuItem>
                    <MenuItem value="REJECTED">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Cancel sx={{ color: theme.palette.error.main }} />
                        <Typography>Red</Typography>
                      </Stack>
                    </MenuItem>
                  </Select>
                </FormControl>

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Değerlendirme Dökümanı
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                      border: `1px dashed ${theme.palette.divider}`,
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    <SingleFileUpload
                      onUploadComplete={handleFileUpload}
                      existFilePath={formDocument}
                      fileUploadPath="committee"
                      acceptedFileTypes={['image/*', 'application/pdf', '.doc,.docx']}
                    />
                  </Paper>
                </Box>
              </Stack>
            </Grid>

            {!isDisabled && (
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={handleCommitteeSubmit}
                  disabled={!formDescription?.trim()}
                  sx={{
                    px: 4,
                    py: 1,
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark,
                    },
                    '&.Mui-disabled': {
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                    },
                  }}
                >
                  Değerlendirmeyi Kaydet
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>
      </Card>
    );
  };

  const renderCommitteeSummary = () => {
    if (!adminInfo?.type?.includes('SUPER_ADMIN')) return null;

    const handleResendVerification = async (committeeId) => {
      try {
        const formData = new URLSearchParams();
        formData.append('startupId', startupId);
        formData.append('committeeId', committeeId);

        const response = await ApiHelper.post('/admin/resend-committee-token', formData.toString(), {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        });

        if (response.data.code === '00000') {
          toast.success('Doğrulama maili başarıyla gönderildi');
        } else {
          toast.error(response.data.message || 'Doğrulama maili gönderilirken bir hata oluştu');
        }
      } catch (error) {
        console.error('Failed to resend verification:', error);
        toast.error(error.response?.data?.message || 'Doğrulama maili gönderilirken bir hata oluştu');
      }
    };

    return (
      <Card sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Komite Değerlendirmeleri
          </Typography>
          
          {committees?.map((committee) => (
            <Accordion 
              key={committee.committeeId}
              sx={{
                '&:not(:last-child)': { mb: 1 },
                '&:before': { display: 'none' },
                borderRadius: 1,
                boxShadow: 'none',
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  '&.Mui-expanded': {
                    minHeight: 48,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                  <Typography sx={{ flex: 1 }}>
                    {committee.memberName}
                  </Typography>
                  <Chip
                    size="small"
                    label={committee.status === 'APPROVED' ? 'Onaylandı' : 
                           committee.status === 'REJECTED' ? 'Reddedildi' : 
                           'Beklemede'}
                    color={committee.status === 'APPROVED' ? 'success' : 
                           committee.status === 'REJECTED' ? 'error' : 
                           'warning'}
                    sx={{ minWidth: 100 }}
                  />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {committee.documentPath && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Değerlendirme Dökümanı
                      </Typography>
                      <Link 
                        href={getFileLink(committee.documentPath)} 
                        target="_blank"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          color: theme.palette.primary.main,
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        <PictureAsPdf fontSize="small" />
                        Dökümanı Görüntüle
                      </Link>
                    </Box>
                  )}

                  <Box>
                    <Stack spacing={1}>
                      {committee.lastSentTime && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarToday fontSize="small" />
                          Son mail gönderimi: {new Date(Number(committee.lastSentTime)).toLocaleString('tr-TR')}
                        </Typography>
                      )}
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => handleResendVerification(committee.committeeId)}
                        startIcon={<Email />}
                      >
                        Doğrulama Mailini Yeniden Gönder
                      </Button>
                    </Stack>
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </CardContent>
      </Card>
    );
  };

  // Add this function to handle user details display
  const renderUserDetails = () => {
    const { userDetails, individualUser, corporateUser } = selectedStartup;
    
    if (!userDetails) return null;

    const isIndividual = userDetails.userType === 'individual';
    const user = isIndividual ? individualUser : corporateUser;

    if (!user) return null;

    return (
      <Card sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Girişimci Bilgileri</Typography>
          <Stack spacing={3}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Person />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {isIndividual ? 'Ad Soyad' : 'Şirket Adı'}
                  </Typography>
                  <Typography variant="body1">
                    {isIndividual ? `${user.name} ${user.lastName}` : user.title}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Box>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Phone />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Telefon
                  </Typography>
                  <Typography variant="body1">
                    {`+${userDetails.countryCode} ${userDetails.phone}`}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {isIndividual ? (
              <Box>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <CalendarToday />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Doğum Tarihi
                    </Typography>
                    <Typography variant="body1">
                      {new Date(user.birthDate).toLocaleDateString('tr-TR')}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            ) : (
              <>
                <Box>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Business />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Vergi Dairesi
                      </Typography>
                      <Typography variant="body1">
                        {user.taxOffice}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
                <Box>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <AccountBalance />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Vergi Numarası
                      </Typography>
                      <Typography variant="body1">
                        {user.taxNumber}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </>
            )}

            <Box>
              <Stack direction="row" alignItems="center" spacing={2}>
                <CalendarToday />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Kayıt Tarihi
                  </Typography>
                  <Typography variant="body1">
                    {new Date(Number(userDetails.registerTime)).toLocaleDateString('tr-TR')}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  // Add new function to fetch KFS preview
  const handleKfsPreviewClick = async () => {
    try {
      const response = await ApiHelper.get(`/admin/startup-kfs-request-prepare/${startupId}`);
      if (response.data && response.data.code === '00000') {
        setKfsPreviewData(response.data.kfsRequstPreview);
        setOpenKfsPreviewDialog(true);
      } else {
        toast.error('KFS önizleme bilgileri alınamadı');
      }
    } catch (error) {
      console.error('KFS preview error:', error);
      toast.error('KFS önizleme bilgileri alınırken bir hata oluştu');
    }
  };

  // Add this new function to calculate remaining time
  const calculateTimeLeft = () => {
    if (!selectedStartup?.endDate) return null;
    
    const endTime = Number(selectedStartup.endDate);
    const now = Date.now();
    const difference = endTime - now;
    
    if (difference <= 0) return null;
    
    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);
    
    return {
      days,
      hours,
      minutes,
      seconds
    };
  };

  // Add new useEffect for countdown
  useEffect(() => {
    if (selectedStartup?.status === 'APPROVED') {
      const timer = setInterval(() => {
        setCountdown(calculateTimeLeft());
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [selectedStartup]);

  // Add new useEffect for fetching approved investments
  useEffect(() => {
    const fetchApprovedInvestments = async () => {
      if (selectedStartup?.status === 'APPROVED' && adminInfo?.type === 'SUPER_ADMIN') {
        setLoadingInvestments(true);
        try {
          const response = await ApiHelper.get(`/admin/approved-investments-by-startup-id/${startupId}`);
          if (response.data && response.data.code === '00000') {
            setApprovedInvestments(response.data.investments);
          }
        } catch (error) {
          console.error('Error fetching approved investments:', error);
          toast.error('Yatırım bilgileri alınırken bir hata oluştu');
        } finally {
          setLoadingInvestments(false);
        }
      }
    };

    fetchApprovedInvestments();
  }, [startupId, selectedStartup?.status, adminInfo?.type]);

  // Update the Excel export function
  const exportToExcel = () => {
    // Prepare the data
    const headers = ['Yatırımcı Adı', 'TC Kimlik No', 'Yatırım Tutarı', 'Ödeme Tipi', 'Onay Tarihi'];
    const data = approvedInvestments.map(({ investment, user }) => [
      user.name,
      user.identityNumber,
      `${Number(investment.amount).toLocaleString('tr-TR')} ₺`,
      investment.paymentType === 'BANK_TRANSFER' ? 'Banka Transferi' : 'Kredi Kartı',
      new Date(investment.updatedAt).toLocaleString('tr-TR')
    ]);

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);

    // Style the headers
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "4F81BD" } },
      alignment: { horizontal: "center", vertical: "center" }
    };

    // Apply styles to header row
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
      ws[cellRef].s = headerStyle;
    }

    // Set column widths
    ws['!cols'] = [
      { wch: 30 }, // Yatırımcı Adı
      { wch: 15 }, // TC Kimlik No
      { wch: 15 }, // Yatırım Tutarı
      { wch: 15 }, // Ödeme Tipi
      { wch: 20 }  // Onay Tarihi
    ];

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Onaylı Yatırımlar');

    // Generate and download the file
    XLSX.writeFile(wb, `onaylı_yatırımlar_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const fetchCampaignDocuments = async () => {
    try {
      setLoadingDocuments(true);
      const response = await ApiHelper.get(`/admin/startup-documents/${startupId}`);
      if (response.data && response.data.code === '00000') {
        setDocuments(response.data.documents);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Bilgi formları yüklenirken bir hata oluştu');
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleGenerateDocument = async () => {
    try {
    setIsLoading(true)
      const formData = new URLSearchParams();
      formData.append('startupId', startupId);

      await ApiHelper.post('/admin/startup-document', formData.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      
      toast.success('Yeni bilgi formu başarıyla oluşturuldu');
      fetchCampaignDocuments(); // Refresh documents list
    } catch (error) {
      console.error('Error generating document:', error);
      toast.error('Bilgi formu oluşturulurken bir hata oluştu');
    } finally {
      setIsLoading(false)
    }
  };

  // Load documents when dialog opens
  useEffect(() => {
    if (openDocumentsDialog) {
      fetchCampaignDocuments();
    }
  }, [openDocumentsDialog]);

  // Add new useEffect to fetch pre-fund data
  useEffect(() => {
    const fetchPreFundData = async () => {
      if (selectedStartup?.isPreFundingActive) {
        try {
          const response = await ApiHelper.get(`/admin/pre-fund-investment/${startupId}`);
          if (response.data?.code === "00000") {
            setPreFundData(response.data.data);
          }
        } catch (error) {
          console.error('Error fetching pre-fund data:', error);
        }
      }
    };

    fetchPreFundData();
    
  }, [startupId, selectedStartup?.isPreFundingActive]);

  // Add new useEffect to fetch pre-fund investment info data
  useEffect(() => {
    const fetchPreFundDataInfo = async () => {
      if (selectedStartup?.isPreFundingActive) {
        try {
          setLoadingPreFundDataInfo(true);
          const response = await ApiHelper.get(`/admin/pre-fund-investment-info/${startupId}`);
          if (response.data?.code === "00000") {
            setPreFundDataInfo(response.data.data);
            console.log('Pre-fund investment info response:', response.data);
            console.log('Pre-fund investment info data:', response.data.data);
          } else {
            console.error('Error in pre-fund investment info response:', response.data);
            setPreFundDataInfo(null);
          }
        } catch (error) {
          console.error('Error fetching pre-fund investment info:', error);
          setPreFundDataInfo(null);
          toast.error('Pre-fund yatırım bilgisi yüklenirken bir hata oluştu');
        } finally {
          setLoadingPreFundDataInfo(false);
        }
      } else {
        setPreFundDataInfo(null);
        setLoadingPreFundDataInfo(false);
      }
    };

    fetchPreFundDataInfo();
  }, [startupId, selectedStartup?.isPreFundingActive]);


  if (loading) return <LinearProgress />;
  if (!selectedStartup) return <Typography>Startup bulunamadı</Typography>;

  // Status'ü API'den gelen response'un içindeki doğru alandan alalım
  const currentStatus = selectedStartup.status || selectedStartup.startupStatus || 'PENDING';
  console.log('Final Current Status:', currentStatus);
  
  // Status config'i kontrol edelim
  const currentStatusConfig = STATUS_CONFIG[currentStatus];
  if (!currentStatusConfig) {
    console.error('Status not found in config:', currentStatus);
  }
  
  const nextStatusConfig = currentStatusConfig?.next ? STATUS_CONFIG[currentStatusConfig.next] : null;

  // Status rengini al
  const statusColor = theme.palette[currentStatusConfig?.color]?.main || theme.palette.grey[500];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PENDING':
        return (
          <Tooltip title="Beklemede">
            <PendingOutlined sx={{ color: theme.palette.warning.main }} />
          </Tooltip>
        );
      case 'APPROVED':
        return (
          <Tooltip title="Onaylandı">
            <CheckCircleOutline sx={{ color: theme.palette.success.main }} />
          </Tooltip>
        );
      case 'REJECTED':
        return (
          <Tooltip title="Reddedildi">
            <CancelOutlined sx={{ color: theme.palette.error.main }} />
          </Tooltip>
        );
      default:
        return null;
    }
  };

  const areAllCommitteesApproved = committees?.every(
    committee => committee.status === 'APPROVED'
  );

  const handleEvaluationSubmit = async () => {
    try {
      const formData = new URLSearchParams();
      formData.append('startupId', startupId);
      formData.append('evaluation', evaluationText);

      await ApiHelper.post('/admin/update-committee-evaluation', formData.toString(), {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      
      toast.success('Komite değerlendirmesi başarıyla kaydedildi');
      setOpenEvaluationDialog(false);
      dispatch(fetchStartupDetail(startupId)); // Refresh startup details
    } catch (error) {
      console.error('Committee evaluation error:', error);
      toast.error('Komite değerlendirmesi kaydedilirken bir hata oluştu');
    }
  };

  return (
    <Container maxWidth="xl">
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {selectedStartup?.isPreFundingActive && preFundData && (
            <Box sx={{ 
              mb: 4, 
              p: 3, 
              borderRadius: 2, 
              bgcolor: (theme) => alpha(theme.palette.info.main, 0.1),
              border: '1px solid',
              borderColor: 'info.main'
            }}>
              <Stack direction="row" spacing={4} alignItems="center">
                <Box>
                  <Typography variant="subtitle2" color="info.main" gutterBottom>
                    Toplam Ön Talep Sayısı
                  </Typography>
                  <Typography variant="h4">
                    {preFundData.count}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="info.main" gutterBottom>
                    Toplam Ön Talep Tutarı
                  </Typography>
                  <Typography variant="h4">
                    {Number(preFundData.totalAmount).toLocaleString('tr-TR')} ₺
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}

          {/* Ön Talep Yatırımı detaylı bilgileri */}
          {selectedStartup?.isPreFundingActive && preFundDataInfo && (
            <Box sx={{ 
              mb: 4, 
              p: 3, 
              borderRadius: 2, 
              bgcolor: (theme) => alpha(theme.palette.info.main, 0.1),
              border: '1px solid',
              borderColor: 'info.main'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" color="info.main">
                  Ön Talep Yatırımları Detaylı Listesi
                </Typography>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<FileDownload />}
                  onClick={handleExportToExcel}
                  disabled={!preFundDataInfo || preFundDataInfo.length === 0}
                  sx={{
                    bgcolor: 'success.main',
                    '&:hover': {
                      bgcolor: 'success.dark',
                    },
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 'medium'
                  }}
                >
                  Excel Export
                </Button>
              </Box>
              
              {/* Özet Bilgiler */}
              <Box sx={{ 
                display: 'flex', 
                gap: 3, 
                mb: 3, 
                p: 2, 
                borderRadius: 1,
                bgcolor: alpha(theme.palette.info.main, 0.05),
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
              }}>
                <Box>
                  <Typography variant="caption" color="info.main" display="block">
                    Toplam Yatırım Sayısı
                  </Typography>
                  <Typography variant="h6" color="info.main" fontWeight="bold">
                    {preFundDataInfo ? preFundDataInfo.length : 0}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="info.main" display="block">
                    Toplam Yatırım Tutarı
                  </Typography>
                  <Typography variant="h6" color="info.main" fontWeight="bold">
                    {preFundDataInfo ? 
                      Number(preFundDataInfo.reduce((sum, item) => sum + Number(item.amount || 0), 0)).toLocaleString('tr-TR') + ' ₺' 
                      : '0 ₺'
                    }
                  </Typography>
                </Box>
              </Box>
              
              {loadingPreFundDataInfo ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={40} />
                </Box>
              ) : preFundDataInfo && preFundDataInfo.length > 0 ? (
                <TableContainer component={Paper} sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
                  <Table sx={{ minWidth: 650 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', color: 'info.main', fontSize: '0.875rem', width: '15%' }}>Yatırım ID</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: 'info.main', fontSize: '0.875rem', width: '25%' }}>Kampanya Adı</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: 'info.main', fontSize: '0.875rem', width: '25%' }}>Yatırımcı Adı</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: 'info.main', fontSize: '0.875rem', width: '20%' }} align="right">Yatırım Tutarı</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: 'info.main', fontSize: '0.875rem', width: '15%' }}>Güncelleme Tarihi</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {preFundDataInfo.map((item, index) => (
                        <TableRow 
                          key={item.investmentId || index} 
                          sx={{ 
                            '&:nth-of-type(odd)': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
                            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
                          }}
                        >
                          <TableCell sx={{ fontSize: '0.875rem' }}>
                            {item.investmentId ? item.investmentId.substring(0, 8) + '...' : '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem' }}>
                            <Tooltip title={item.startupName || '-'}>
                              <Typography noWrap sx={{ maxWidth: 200 }}>
                                {item.startupName || '-'}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem' }}>
                            <Tooltip title={item.investorName || '-'}>
                              <Typography noWrap sx={{ maxWidth: 200 }}>
                                {item.investorName || '-'}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem', fontWeight: 'medium' }} align="right">
                            {item.amount ? Number(item.amount).toLocaleString('tr-TR') + ' ₺' : '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem' }}>
                            {item.updatedAt ? new Date(item.updatedAt).toLocaleString('tr-TR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 2 }}>
                  Henüz ön talep yatırımı bulunmamaktadır.
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" gutterBottom>
              {selectedStartup?.name || 'Kampanya Detayı'}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={STATUS_CONFIG[selectedStartup?.status]?.label || selectedStartup?.status}
                color={STATUS_CONFIG[selectedStartup?.status]?.color || 'default'}
              />
            </Stack>
          </Box>

          {/* Kısa Link Yolu - her zaman görünür */}
          <Card sx={{ borderRadius: 2, mb: 3 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Kısa Link Yolu (Shortlink Path)
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="flex-start" spacing={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Path"
                  placeholder="örn: arbalet"
                  value={shortlinkPathInput}
                  onChange={handleShortlinkPathChange}
                  error={Boolean(shortlinkPathError)}
                  helperText={shortlinkPathError || 'Sadece harf, rakam ve tire. Örnek: arbalet'}
                  inputProps={{ maxLength: 100 }}
                  sx={{ maxWidth: 320 }}
                />
                <Button
                  variant="contained"
                  onClick={handleSaveShortlinkPath}
                  disabled={shortlinkPathSaving}
                  sx={{ minWidth: 120, alignSelf: { xs: 'flex-start', sm: 'flex-end' } }}
                >
                  {shortlinkPathSaving ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
              </Stack>
              {shortlinkPathInput && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  kf.ardventure.com.tr/{shortlinkPathInput || '…'}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Status buttons moved here, right after the header */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
            {adminInfo?.type === 'SUPER_ADMIN' && (
              <>
                {selectedStartup?.status === 'WAITING_REVIEW' && (
                  <>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<Cancel />}
                      onClick={() => setOpenRejectDialog(true)}
                    >
                      Reddet
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<CalendarMonth />}
                      onClick={() => setOpenDateDialog(true)}
                    >
                      Kampanya Tarihlerini Belirle
                    </Button>
                  </>
                )}
                {selectedStartup?.status === 'WAITING_MKK_APPROVAL' && (
                  <Button
                    variant="contained"
                    color="info"
                    onClick={handleKfsPreviewClick}
                    startIcon={<PictureAsPdf />}
                  >
                    KFS Önizleme
                  </Button>
                )}
                {selectedStartup?.status === 'WAITING_INVESTMENT_COMMITTEE' && (
                  <Button
                    variant="contained"
                    color="primary"
                    endIcon={<ArrowForward />}
                    onClick={() => handleStatusChange(STATUS_CONFIG[selectedStartup?.status].next)}
                  >
                    {STATUS_CONFIG[selectedStartup?.status].nextLabel}
                  </Button>
                )}
                {selectedStartup?.status === 'APPROVED' && (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 2
                  }}>
                    {adminInfo?.type === 'SUPER_ADMIN' && (
                      <Box sx={{ 
                        display: 'flex', 
                        gap: 2,
                        justifyContent: 'flex-start'
                      }}>
                        <Button
                          variant="contained"
                          color={selectedStartup.isFundable ? "error" : "success"}
                          startIcon={selectedStartup.isFundable ? <Cancel /> : <CheckCircle />}
                          onClick={() => handleFundingControl(selectedStartup.isFundable ? 'stop' : 'open')}
                          sx={{
                            minWidth: 200,
                            px: 3,
                            py: 1,
                            bgcolor: selectedStartup.isFundable ? theme.palette.error.main : theme.palette.success.main,
                            '&:hover': {
                              bgcolor: selectedStartup.isFundable ? theme.palette.error.dark : theme.palette.success.dark,
                            },
                          }}
                        >
                          {selectedStartup.isFundable ? 'Fonlamayı Durdur' : 'Fonlamayı Başlat'}
                        </Button>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircle />}
                          onClick={() => setOpenSuccessFinishDialog(true)}
                          sx={{
                            minWidth: 200,
                            px: 3,
                            py: 1,
                            bgcolor: theme.palette.success.main,
                            '&:hover': {
                              bgcolor: theme.palette.success.dark,
                            },
                          }}
                        >
                          Başarılı Sonlandır
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          startIcon={<Cancel />}
                          onClick={() => setOpenFailureFinishDialog(true)}
                          sx={{
                            minWidth: 200,
                            px: 3,
                            py: 1,
                            bgcolor: theme.palette.error.main,
                            '&:hover': {
                              bgcolor: theme.palette.error.dark,
                            },
                          }}
                        >
                          Başarısız Sonlandır
                        </Button>
                      </Box>
                    )}
                    {countdown && (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        gap: 1,
                        py: 1,
                        px: 2,
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                      }}>
                        <CalendarToday sx={{ color: theme.palette.warning.main }} />
                        <Typography variant="subtitle1" color="warning.main" fontWeight="medium">
                          Kalan Süre: {String(countdown.days).padStart(2, '0')} gün {String(countdown.hours).padStart(2, '0')}:
                          {String(countdown.minutes).padStart(2, '0')}:
                          {String(countdown.seconds).padStart(2, '0')}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
                {adminInfo?.type === 'SUPER_ADMIN' && selectedStartup?.status === 'LAST_VIEW' && (
                  <Box sx={{ mb: 3 }}>
                    <Card sx={{ p: 3, borderRadius: 2 }}>
                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <CheckCircle sx={{ color: theme.palette.success.main }} />
                          <Typography variant="h6">
                            Kampanya Yayına Hazır
                          </Typography>
                        </Box>
                        <Typography color="text.secondary">
                          Tüm onay süreçleri tamamlandı. Kampanyayı yayınlamaya hazırsınız. Yayınlama işleminden sonra kampanya aktif olarak yatırımcılara açılacaktır.
                        </Typography>
                        <Box>
                          <Button
                            variant="contained"
                            color="success"
                            onClick={() => handleStatusChange()}
                            startIcon={<CheckCircle />}
                            sx={{
                              px: 4,
                              py: 1,
                              bgcolor: theme.palette.success.main,
                              '&:hover': {
                                bgcolor: theme.palette.success.dark,
                              },
                            }}
                          >
                            Kampanyayı Yayınla
                          </Button>
                        </Box>
                      </Stack>
                    </Card>
                  </Box>
                )}
                {!selectedStartup?.isPreFundingActive ? (
                  <Button
                  variant="contained"
                  color="warning"
                  startIcon={<CalendarMonth />}
                  onClick={() => setOpenPreFundDialog(true)}
                >
                  Ön Talep Yatırımını Aç
                </Button>
                ) : <Button
                variant="contained"
                color="warning"
                startIcon={<CalendarMonth />}
                onClick={async () => {
                  try {
                    await dispatch(deactivePreFunding({
                      startupId,
                    })).unwrap();
    
                    toast.success('Kampanya ön talebi başarıyla kapatıldı');
                    dispatch(fetchStartupDetail(startupId));
                  } catch (error) {
                    console.error('Campaign dates update error:', error);
                    toast.error('Kampanya ön talebi başarıyla kapatıldı');
                  }
                }}
              >
                Ön Talep Yatırımını Kapat
              </Button>}
              {selectedStartup?.isShowing ? (
                <Button variant='contained' color='error' onClick={async () => {
                  try {
                    await dispatch(deactiveShowing({
                      startupId,
                    })).unwrap();
    
                    toast.success('Kampanya kartı yayından kaldırıldı');
                    dispatch(fetchStartupDetail(startupId));
                  } catch (error) {
                    console.error('Campaign dates update error:', error);
                    toast.error('Kampanya kartı yayından kaldırılırken bir hata oluştu');
                  }
                }}>
                  Kampanya kartını yayından kaldır
                </Button>
              ) : (
                <Button variant='contained' color='success' onClick={async () => {
                  try {
                    await dispatch(activeShowing({
                      startupId,
                    })).unwrap();
    
                    toast.success('Kampanya kartı yayına alındı');
                    dispatch(fetchStartupDetail(startupId));
                  } catch (error) {
                    console.error('Campaign dates update error:', error);
                    toast.error('Kampanya kartı yayına alınırken bir hata oluştu');
                  }
                }}>
                  Kampanya kartını yayına al
                </Button>
              ) }
              {selectedStartup?.isDetailActive ? (
                <Button variant='contained' color='error' onClick={async () => {
                  try {
                    await dispatch(deactiveDetail({
                      startupId,
                    })).unwrap();
    
                    toast.success('Kampanya detayını yayından kaldırıldı');
                    dispatch(fetchStartupDetail(startupId));
                  } catch (error) {
                    console.error('Campaign dates update error:', error);
                    toast.error('Kampanya detayını yayından kaldırılırken bir hata oluştu');
                  }
                }}>
                  Kampanya detayını yayından kaldır
                </Button>
              ) : (
                <Button variant='contained' color='success' onClick={async () => {
                  try {
                    await dispatch(activeDetail({
                      startupId,
                    })).unwrap();
    
                    toast.success('Kampanya detayını yayına alındı');
                    dispatch(fetchStartupDetail(startupId));
                  } catch (error) {
                    console.error('Campaign dates update error:', error);
                    toast.error('Kampanya detayını yayına alınırken bir hata oluştu');
                  }
                }}>
                  Kampanya detayını yayına al
                </Button>
              )}
              </>
            )}
          </Box>

          {/* Rejection reason card moved here */}
          {selectedStartup?.status === 'REJECTED' && selectedStartup?.rejectedReason && (
            <Card sx={{ mb: 3, bgcolor: alpha(theme.palette.error.main, 0.1) }}>
              <CardContent>
                <Typography variant="h6" color="error" gutterBottom>
                  Reddedilme Sebebi:
                </Typography>
                <Typography>
                  {selectedStartup.rejectedReason}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Komite Değerlendirme Formu - Komite üyeleri için */}
          {selectedStartup?.status === 'WAITING_INVESTMENT_COMMITTEE' && 
           adminInfo?.type?.includes('COMMITTEE') && 
           renderCommitteeApprovalForm()}

          {/* Komite Özeti - SUPER_ADMIN için */}
          {selectedStartup?.status === 'WAITING_INVESTMENT_COMMITTEE' && 
           adminInfo?.type?.includes('SUPER_ADMIN') && 
           renderCommitteeSummary()}

          {/* Header Section */}
          <Card
            sx={{
              mb: 4,
              borderRadius: 2,
              position: 'relative',
              overflow: 'visible',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `radial-gradient(circle at top right, ${alpha(statusColor, 0.12)}, transparent 70%)`,
                borderRadius: 'inherit',
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item>
                  <Avatar
                    src={getFileLink(selectedStartup.startupProfile?.mainLogoPath)}
                    alt={selectedStartup.startupProfile?.name}
                    sx={{ width: 80, height: 80 }}
                  />
                </Grid>
                <Grid item xs>
                  <Typography variant="h4" gutterBottom>
                    {selectedStartup.startupProfile?.name}
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    {selectedStartup.startupProfile?.promotionName}
                  </Typography>
                  
                  {selectedStartup?.status === 'WAITING_INVESTMENT_COMMITTEE' && (
                    <Stack direction="row" spacing={3} sx={{ mt: 3 }}>
                      {committees.map((committee) => (
                        <Box
                          key={committee.committeeId}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1,
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.background.default, 0.6),
                          }}
                        >
                          {getStatusIcon(committee.status)}
                          <Typography variant="body2">
                            {committee.memberName}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}

                  {/* Status Section */}
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
                    <Chip
                      label={currentStatusConfig?.label || `Durum: ${currentStatus}`}
                      color={currentStatusConfig?.color || 'default'}
                      sx={{ 
                        px: 2,
                        py: 2,
                        borderRadius: 2,
                        '& .MuiChip-label': {
                          fontSize: '1rem',
                          fontWeight: 'bold'
                        }
                      }}
                    />
                    
                    {nextStatusConfig && (
                      <>
                        <ArrowForward color="action" />
                        <Chip
                          label={nextStatusConfig.label}
                          color={nextStatusConfig.color}
                          variant="outlined"
                          sx={{ 
                            px: 2,
                            py: 2,
                            borderRadius: 2,
                            opacity: 0.7,
                            '& .MuiChip-label': {
                              fontSize: '1rem'
                            }
                          }}
                        />
                        
                        <Button
                          variant="contained"
                          color={nextStatusConfig.color}
                          onClick={handleStatusChange}
                          disabled={loading || (currentStatus === 'WAITING_INVESTMENT_COMMITTEE' && !areAllCommitteesApproved)}
                          sx={{ 
                            px: 3,
                            py: 1,
                            borderRadius: 2,
                            boxShadow: 2
                          }}
                        >
                          {currentStatusConfig?.nextLabel}
                        </Button>
                      </>
                    )}
                  </Stack>
                </Grid>
                <Grid item>
                  <Stack direction="column" spacing={2}>
                    <Button
                      variant="contained"
                      startIcon={<MonetizationOn />}
                      onClick={() => navigate(`/startup/yatirimlar/${selectedStartup.startupId}`)}
                      sx={{
                        bgcolor: theme.palette.success.main,
                        '&:hover': {
                          bgcolor: theme.palette.success.dark,
                        },
                      }}
                    >
                      Yatırımları Görüntüle
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Person />}
                      onClick={() => navigate('/kullanici/6a1e19bd-c4ca-48c7-bd3b-9b75efd4bfa7')}
                      sx={{
                        bgcolor: theme.palette.info.main,
                        '&:hover': {
                          bgcolor: theme.palette.info.dark,
                        },
                      }}
                    >
                      Girişimci Profilini Görüntüle
                    </Button>
                    {adminInfo?.type === 'SUPER_ADMIN' && (
                      <Button
                        variant="contained"
                        startIcon={<Edit />}
                        onClick={() => setOpenEvaluationDialog(true)}
                        sx={{
                          bgcolor: theme.palette.warning.main,
                          '&:hover': {
                            bgcolor: theme.palette.warning.dark,
                          },
                        }}
                      >
                        Komite Değerlendirme Raporu
                      </Button>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Campaign Dates Section - Only for SUPER_ADMIN */}
          {adminInfo?.type === 'SUPER_ADMIN' && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={8}>
                <Card sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                      <CalendarMonth color="primary" />
                      <Typography variant="h6">
                        Kampanya Tarihleri
                      </Typography>
                      {selectedStartup?.status === 'WAITING_REVIEW' && (
                        <Button
                          size="small"
                          startIcon={<Edit />}
                          onClick={() => setOpenDateDialog(true)}
                          sx={{ ml: 'auto' }}
                        >
                          Düzenle
                        </Button>
                      )}
                    </Stack>
                    <Grid container spacing={4}>
                      <Grid item xs={12} sm={6}>
                        <Stack spacing={1}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Başlangıç Tarihi
                          </Typography>
                          <Typography variant="body1">
                            {selectedStartup?.startDate 
                              ? new Date(Number(selectedStartup.startDate)).toLocaleString('tr-TR', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                              : 'Belirlenmedi'}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Stack spacing={1}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Bitiş Tarihi
                          </Typography>
                          <Typography variant="body1">
                            {selectedStartup?.endDate
                              ? new Date(Number(selectedStartup.endDate)).toLocaleString('tr-TR', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                              : 'Belirlenmedi'}
                          </Typography>
                        </Stack>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ borderRadius: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
                  <CardContent sx={{ width: '100%' }}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<PictureAsPdf />}
                      onClick={() => setOpenDocumentsDialog(true)}
                      sx={{
                        py: 2,
                        bgcolor: theme.palette.info.main,
                        '&:hover': {
                          bgcolor: theme.palette.info.dark,
                        },
                      }}
                    >
                      Kampanya Bilgi Formları
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Details Section */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              {selectedStartup.status === 'WAITING_INVESTMENT_COMMITTEE' ? (
                <Card sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                      <PictureAsPdf color="primary" />
                      <Typography variant="h6">
                        Bilgi Formu
                      </Typography>
                    </Stack>
                    {selectedStartup.documentLink ? (
                      <Box sx={{ 
                        p: 4, 
                        textAlign: 'center',
                        border: '2px dashed',
                        borderColor: 'divider',
                        borderRadius: 2,
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03)
                      }}>
                        <PictureAsPdf sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                          Bilgi Formu Oluşturuldu
                        </Typography>
                        <Typography color="text.secondary" sx={{ mb: 3 }}>
                          Kampanya için hazırlanan bilgi formunu inceleyebilirsiniz
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<PictureAsPdf />}
                          href={selectedStartup.documentLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="large"
                          sx={{
                            px: 4,
                            py: 1,
                            boxShadow: 2,
                            '&:hover': {
                              boxShadow: 4
                            }
                          }}
                        >
                          Bilgi Formunu Görüntüle
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ 
                        p: 3, 
                        textAlign: 'center',
                        bgcolor: (theme) => alpha(theme.palette.warning.main, 0.1),
                        borderRadius: 1
                      }}>
                        <Typography color="text.secondary">
                          Bilgi formu henüz oluşturulmamış
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Kampanya Detayları
                    </Typography>
                    <Typography paragraph>
                      {selectedStartup.startupProfile?.shortDescription}
                    </Typography>
                    <Box 
                      sx={{ mt: 3 }}
                      dangerouslySetInnerHTML={{ 
                        __html: selectedStartup.startupProfile?.aboutStartup 
                      }} 
                    />
                  </CardContent>
                </Card>
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Finansal Özet
                  </Typography>
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Toplam Limit
                      </Typography>
                      <Typography variant="h5">
                        {Number(selectedStartup.startupBalance?.totalLimit || 0).toLocaleString('tr-TR')} ₺
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Toplanan Yatırım
                      </Typography>
                      <Typography variant="h5" color="success.main">
                        {Number(selectedStartup.startupBalance?.qualifiedAmount || 0).toLocaleString('tr-TR')} ₺
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({selectedStartup.startupBalance?.qualifiedRate || 0}%)
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Açık Limit
                      </Typography>
                      <Typography variant="h5" color="info.main">
                        {Number(selectedStartup.startupBalance?.availableLimit || 0).toLocaleString('tr-TR')} ₺
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {renderUserDetails()}

          {/* Add KFS Preview Dialog */}
          <Dialog 
            open={openKfsPreviewDialog} 
            onClose={() => setOpenKfsPreviewDialog(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              <Stack direction="row" alignItems="center" spacing={1}>
                <PictureAsPdf color="primary" />
                <Typography variant="h6">KFS Önizleme</Typography>
              </Stack>
            </DialogTitle>
            <DialogContent>
              {kfsPreviewData && (
                <Stack spacing={3} sx={{ mt: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Proje Adı
                    </Typography>
                    <Typography variant="body1">
                      {kfsPreviewData.proejctName}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Proje Tanımı
                    </Typography>
                    <Typography variant="body1">
                      {kfsPreviewData.projectDefinition}
                    </Typography>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Fon Tutarı
                        </Typography>
                        <Typography variant="body1">
                          {Number(kfsPreviewData.fundableAmount).toLocaleString('tr-TR')} ₺
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Ek Fonlama
                        </Typography>
                        <Typography variant="body1">
                          {kfsPreviewData.overFunding === 'Y' ? 'Evet' : 'Hayır'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Başlangıç Tarihi
                        </Typography>
                        <Typography variant="body1">
                          {new Date(kfsPreviewData.projectStartDate).toLocaleString('tr-TR')}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Bitiş Tarihi
                        </Typography>
                        <Typography variant="body1">
                          {new Date(kfsPreviewData.projectEndDate).toLocaleString('tr-TR')}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Nominal Tutar
                      </Typography>
                      <Typography variant="body1">
                        {Number(kfsPreviewData.fundNominalAmount || 0).toLocaleString('tr-TR')} ₺
                      </Typography>
                    </Box>
                  </Grid>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Girişimci Bilgileri
                    </Typography>
                    {kfsPreviewData.saveEntrepreneurInfoSet.map((entrepreneur, index) => (
                      <Paper
                        key={index}
                        variant="outlined"
                        sx={{ p: 2, mt: 1 }}
                      >
                        <Stack spacing={2}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Girişimci Tipi
                            </Typography>
                            <Typography>
                              {entrepreneur.idType === 'VKN' ? 'Tüzel Kişi' : 'Gerçek Kişi'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {entrepreneur.idType === 'TCKN' ? 'TC Kimlik No' : 'Vergi No'}
                            </Typography>
                            <Typography>
                              {entrepreneur.id}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              İsim Soyisim
                            </Typography>
                            <Typography>
                              {entrepreneur.name} {entrepreneur.surname}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              İletişim Bilgileri
                            </Typography>
                            <Typography>
                              {entrepreneur.email} | {entrepreneur.tel}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>

                  {/* Raw JSON Data Section */}
                  <Box sx={{ mt: 3 }}>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Raw JSON Data
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            bgcolor: alpha(theme.palette.background.default, 0.6),
                            '& pre': {
                              margin: 0,
                              overflow: 'auto',
                              maxHeight: '400px'
                            }
                          }}
                        >
                          <pre>
                            {JSON.stringify(kfsPreviewData, null, 2)}
                          </pre>
                        </Paper>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                </Stack>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenKfsPreviewDialog(false)}>
                Kapat
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setOpenKfsPreviewDialog(false);
                  handleStatusChange();
                }}
              >
                KFS'ye Gönder
              </Button>
            </DialogActions>
          </Dialog>

          {/* Add this before the closing Container tag */}
          {selectedStartup?.status === 'APPROVED' && adminInfo?.type === 'SUPER_ADMIN' && (
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                onClick={() => setOpenSuccessFinishDialog(true)}
                sx={{
                  px: 3,
                  py: 1,
                  bgcolor: theme.palette.success.main,
                  '&:hover': {
                    bgcolor: theme.palette.success.dark,
                  },
                }}
              >
                Kampanyayı Başarılı Sonlandır
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<Cancel />}
                onClick={() => setOpenFailureFinishDialog(true)}
                sx={{
                  px: 3,
                  py: 1,
                  bgcolor: theme.palette.error.main,
                  '&:hover': {
                    bgcolor: theme.palette.error.dark,
                  },
                }}
              >
                Kampanyayı Başarısız Sonlandır
              </Button>
            </Box>
          )}

          {/* Add the funding control button here */}

          {/* Committee Evaluation Dialog */}
          <Dialog
            open={openEvaluationDialog}
            onClose={() => setOpenEvaluationDialog(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Edit color="primary" />
                <Typography variant="h6">Komite Değerlendirme Raporu</Typography>
              </Stack>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  label="Değerlendirme"
                  value={evaluationText}
                  onChange={(e) => setEvaluationText(e.target.value)}
                  placeholder="Komite değerlendirme raporunu buraya giriniz..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'background.paper',
                      '&:hover': {
                        '& > fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    },
                  }}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2.5 }}>
              <Button
                onClick={() => setOpenEvaluationDialog(false)}
                color="inherit"
              >
                İptal
              </Button>
              <Button
                onClick={handleEvaluationSubmit}
                variant="contained"
                color="primary"
                disabled={!evaluationText.trim()}
              >
                Kaydet
              </Button>
            </DialogActions>
          </Dialog>

          {/* Campaign Documents Dialog */}
          <Dialog
            open={openDocumentsDialog}
            onClose={() => setOpenDocumentsDialog(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <PictureAsPdf color="primary" />
                  <Typography variant="h6">Kampanya Bilgi Formları</Typography>
                </Stack>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleGenerateDocument}
                  sx={{
                    bgcolor: theme.palette.success.main,
                    '&:hover': {
                      bgcolor: theme.palette.success.dark,
                    },
                  }}
                >
                  Yeni Form Oluştur
                </Button>
              </Stack>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                {loadingDocuments ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : documents.length === 0 ? (
                  <Box sx={{ 
                    p: 3, 
                    textAlign: 'center',
                    bgcolor: (theme) => alpha(theme.palette.warning.main, 0.1),
                    borderRadius: 1
                  }}>
                    <Typography color="text.secondary">
                      Henüz bilgi formu oluşturulmamış
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={2}>
                    {documents.map((doc) => (
                      <Paper
                        key={doc.startupDocumentId}
                        variant="outlined"
                        sx={{
                          p: 2,
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                          },
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <PictureAsPdf color="primary" />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Bilgi Formu
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Oluşturulma: {new Date(Number(doc.registerTime)).toLocaleString('tr-TR')}
                            </Typography>
                          </Box>
                          <Button
                            variant="outlined"
                            startIcon={<FileDownload />}
                            href={doc.documentPath}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            İndir
                          </Button>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2.5 }}>
              <Button
                onClick={() => setOpenDocumentsDialog(false)}
                color="inherit"
              >
                Kapat
              </Button>
            </DialogActions>
          </Dialog>


        </>
      )}

      {/* Status Change Dialog */}
      <Dialog open={openStatusDialog} onClose={() => setOpenStatusDialog(false)}>
        <DialogTitle>Durum Güncelle</DialogTitle>
        <DialogContent sx={{ minWidth: 400, mt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Yeni Durum</InputLabel>
            <Select
              value={newStatus}
              label="Yeni Durum"
              onChange={(e) => setNewStatus(e.target.value)}
            >
              {Object.entries(STATUS_CONFIG).map(([key, { label }]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenStatusDialog(false)}>
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              // Implement the logic to update the status
              setOpenStatusDialog(false);
            }}
            disabled={!newStatus}
          >
            Güncelle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add the rejection dialog */}
      <Dialog open={openRejectDialog} onClose={() => setOpenRejectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Kampanyayı Reddet</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Red Sebebi"
            fullWidth
            multiline
            rows={4}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRejectDialog(false)}>İptal</Button>
          <Button onClick={handleReject} color="error" variant="contained">
            Reddet
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add the campaign dates dialog */}
      <Dialog open={openDateDialog} onClose={() => setOpenDateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Kampanya Tarihlerini Belirle</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Başlangıç Tarihi"
              type="datetime-local"
              value={formatDateForInput(startDate)}
              onChange={(e) => {
                const date = new Date(e.target.value);
                if (!isNaN(date.getTime())) {
                  setStartDate(date.getTime());
                }
              }}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />
            <TextField
              label="Bitiş Tarihi"
              type="datetime-local"
              value={formatDateForInput(endDate)}
              onChange={(e) => {
                const date = new Date(e.target.value);
                if (!isNaN(date.getTime())) {
                  setEndDate(date.getTime());
                }
              }}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDateDialog(false)}>İptal</Button>
          <Button
            onClick={async () => {
              try {
                await dispatch(updateStartupDates({
                  startupId,
                  startDate: String(startDate),
                  endDate: String(endDate)
                })).unwrap();

                toast.success('Kampanya tarihleri başarıyla güncellendi');
                setOpenDateDialog(false);
                dispatch(fetchStartupDetail(startupId));
              } catch (error) {
                console.error('Campaign dates update error:', error);
                toast.error('Kampanya tarihleri güncellenirken bir hata oluştu');
              }
            }}
            variant="contained"
            disabled={!startDate || !endDate}
          >
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      {/* PreFund Dialog */}
      <Dialog open={preFundDialog} onClose={() => setOpenPreFundDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ön Talep Bitiş Tarihini Belirle</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Ön Talep Bitiş Tarihi"
              type="datetime-local"
              value={formatDateForInput(preFundEndDate)}
              onChange={(e) => {
                const date = new Date(e.target.value);
                // Add 6 hours to compensate for timezone difference
                if (!isNaN(date.getTime())) {
                  setPreFundEndDate(date.getTime() + (3 * 60 * 60 * 1000));
                }
              }}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPreFundDialog(false)}>İptal</Button>
          <Button
            onClick={async () => {
              try {
                await dispatch(activePreFunding({
                  startupId,
                  endDate: String(preFundEndDate)
                })).unwrap();

                toast.success('Kampanya ön talebi başarıyla aktif edildi.');
                setOpenPreFundDialog(false);
                dispatch(fetchStartupDetail(startupId));
              } catch (error) {
                console.error('Campaign dates update error:', error);
                toast.error('Kampanya ön talebe açılırken bir hata oluştu');
              }
            }}
            variant="contained"
            disabled={!preFundEndDate}
          >
            Ön Yatırıma Aç
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add this before the closing Container tag */}
      <Dialog
        open={openPublishDialog}
        onClose={() => setOpenPublishDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Warning color="warning" />
            <Typography variant="h6">Kampanyayı Yayınla</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 2 }}>
            Bu kampanyayı yayınlamak üzeresiniz. Bu işlem geri alınamaz ve kampanya yatırımcılara açılacaktır. 
            Devam etmek istediğinizden emin misiniz?
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.warning.main, 0.1), borderRadius: 1 }}>
            <Typography variant="subtitle2" color="warning.main">
              Not: Kampanya yayınlandıktan sonra temel bilgilerde değişiklik yapılamayacaktır.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button 
            onClick={() => setOpenPublishDialog(false)}
            color="inherit"
          >
            İptal
          </Button>
          <Button
            onClick={handlePublishConfirm}
            variant="contained"
            color="success"
            startIcon={<CheckCircle />}
          >
            Evet, Yayınla
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add these before the closing Container tag */}
      <Dialog
        open={openSuccessFinishDialog}
        onClose={() => setOpenSuccessFinishDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckCircle color="success" />
            <Typography variant="h6">Kampanyayı Başarılı Sonlandır</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 2 }}>
            Bu kampanyayı başarılı olarak sonlandırmak üzeresiniz. Bu işlem geri alınamaz ve kampanya tamamlanmış olarak işaretlenecektir.
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 1 }}>
            <Typography variant="subtitle2" color="success.main">
              Not: Bu işlem geri alınamaz ve kampanya başarıyla tamamlanmış olarak işaretlenecektir.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button 
            onClick={() => setOpenSuccessFinishDialog(false)}
            color="inherit"
          >
            İptal
          </Button>
          <Button
            onClick={() => handleFinishCampaign('SUCCESS')}
            variant="contained"
            color="success"
            startIcon={<CheckCircle />}
          >
            Başarılı Sonlandır
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openFailureFinishDialog}
        onClose={() => setOpenFailureFinishDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Warning color="error" />
            <Typography variant="h6">Kampanyayı Başarısız Sonlandır</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 2 }}>
            Bu kampanyayı başarısız olarak sonlandırmak üzeresiniz. Bu işlem geri alınamaz ve kampanya başarısız olarak işaretlenecektir.
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.error.main, 0.1), borderRadius: 1 }}>
            <Typography variant="subtitle2" color="error.main">
              Uyarı: Bu işlem geri alınamaz ve kampanya başarısız olarak işaretlenecektir.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button 
            onClick={() => setOpenFailureFinishDialog(false)}
            color="inherit"
          >
            İptal
          </Button>
          <Button
            onClick={() => handleFinishCampaign('FAILURE')}
            variant="contained"
            color="error"
            startIcon={<Cancel />}
          >
            Başarısız Sonlandır
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add this right before the Campaign Dates Card in the render section */}
      {adminInfo?.type === 'SUPER_ADMIN' && selectedStartup?.status === 'APPROVED' && (
        <Card sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
              <Typography variant="h6">
                Onaylı Yatırımlar
              </Typography>
              <Button
                variant="outlined"
                startIcon={<FileDownload />}
                onClick={exportToExcel}
                disabled={loadingInvestments || approvedInvestments.length === 0}
              >
                Excel'e Aktar
              </Button>
            </Stack>
            
            {loadingInvestments ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : approvedInvestments.length === 0 ? (
              <Box sx={{ 
                p: 3, 
                textAlign: 'center',
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.1),
                borderRadius: 1
              }}>
                <Typography color="text.secondary">
                  Henüz onaylı yatırım bulunmamaktadır
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Yatırımcı Adı</TableCell>
                      <TableCell>TC Kimlik No</TableCell>
                      <TableCell align="right">Yatırım Tutarı</TableCell>
                      <TableCell>Ödeme Tipi</TableCell>
                      <TableCell>Onay Tarihi</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {approvedInvestments.map(({ investment, user }) => (
                      <TableRow key={investment.investmentId}>
                        <TableCell>{user.name}</TableCell>
                        <TableCell>{user.identityNumber}</TableCell>
                        <TableCell align="right">
                          {Number(investment.amount).toLocaleString('tr-TR')} ₺
                        </TableCell>
                        <TableCell>
                          {investment.paymentType === 'BANK_TRANSFER' ? 'Banka Transferi' : 'Kredi Kartı'}
                        </TableCell>
                        <TableCell>
                          {new Date(investment.updatedAt).toLocaleString('tr-TR')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}
    </Container>
  );
}

export default StartupDetailPage;